<script setup>
import { computed, reactive, ref, watch } from 'vue';
import XlButton from '../xl/XlButton.vue';
import XlTag from '../xl/XlTag.vue';
import { writeSeat } from '../../helpers/sessionSeat';
import { clock, longDay, seatSavedLine, seriesLabel } from '../../helpers/sessionTimes';

/**
 * Every date of a repeating session, on its page — so it is clear what was
 * scheduled: those that have been, those called off, the next one, and the
 * rest. A date still to come carries a seat for that date alone, beside the
 * page's own button, which covers the series.
 */
const props = defineProps({
  series: { type: Object, required: true },
  timeZone: { type: String, required: true },
});
const emit = defineEmits(['seat', 'gone']);

// Shown at once, put back on failure; a date mid-write ignores the parent's patches.
const seats = reactive({});
const busy = reactive({});
const note = ref('');
watch(() => props.series.dates, (dates) => {
  for (const date of dates) if (!busy[date.id]) seats[date.id] = Boolean(date.seat);
}, { immediate: true, deep: true });

async function toggle(date) {
  if (busy[date.id]) return;
  const on = !seats[date.id];
  busy[date.id] = true;
  seats[date.id] = on;
  const outcome = await writeSeat(date.id, on, { series: false });
  busy[date.id] = false;

  if (outcome === 'saved' || outcome === 'released') {
    note.value = on ? seatSavedLine(date, new Date(), props.timeZone) : 'Seat canceled.';
    emit('seat', { id: date.id, seat: on });
  }
  else {
    seats[date.id] = !on;
    if (outcome === 'gone') emit('gone', date.id);
  }
}

const when = (date) => `${longDay(date.starts_at, props.timeZone)} · ${clock(date.starts_at, props.timeZone)}`;

// An older payload has no `state`: every date it sends is still to come.
const stateOf = (date) => date.state ?? 'upcoming';
const ahead = (date) => ['next', 'upcoming'].includes(stateOf(date));
const NOTES = { done: 'Done', cancelled: 'Canceled', next: 'Next' };

/**
 * Two lists, in date order, as on the staff board (QA): what already
 * happened, on its real day, and what is coming up. A canceled date still to
 * come is listed with what is coming, marked; one already past is neither.
 */
const isAhead = (date) => new Date(date.starts_at) > new Date();
const groups = computed(() => {
  const dates = props.series.dates ?? [];
  return [
    { key: 'happened', label: 'Already happened', dates: dates.filter((d) => stateOf(d) === 'done') },
    { key: 'coming', label: 'Coming up', dates: dates.filter((d) => ahead(d) || (stateOf(d) === 'cancelled' && isAhead(d))) },
  ].filter((group) => group.dates.length);
});
const TONES = { done: '', cancelled: 'error', next: 'brand' };
</script>

<template>
  <section class="dates" data-testid="series-dates">
    <div class="dates__sh"><h2>{{ seriesLabel(series) }}</h2><span class="dates__ln" /></div>
    <template v-for="group in groups" :key="group.key">
    <p class="dates__group" :data-testid="`series-dates-${group.key}`">{{ group.label }}</p>
    <ul class="dates__card">
      <li
        v-for="date in group.dates"
        :key="date.id"
        class="dates__row"
        :class="`dates__row--${stateOf(date)}`"
        :data-state="stateOf(date)"
        data-testid="series-date"
      >
        <span class="dates__when">
          {{ when(date) }}
          <XlTag v-if="NOTES[stateOf(date)]" :tone="TONES[stateOf(date)]" data-testid="series-date-state">{{ NOTES[stateOf(date)] }}</XlTag>
        </span>
        <XlButton
          v-if="ahead(date)"
          :variant="seats[date.id] ? 'ghost' : 'filled'"
          size="sm"
          data-testid="date-seat"
          @click="toggle(date)"
        >{{ seats[date.id] ? 'Seat saved' : 'Save my seat' }}<span class="dates__sr">: {{ when(date) }}</span></XlButton>
      </li>
    </ul>
    </template>
    <p class="dates__sr" aria-live="polite">{{ note }}</p>
  </section>
</template>

<style scoped>
/* Prototype `.sh` and `.card` + `.row` (build 30 Sep 12:40). */
.dates__sh { display: flex; align-items: baseline; gap: 12px; margin: 34px 0 14px; }
.dates__group { margin: 14px 0 8px; font-size: 12.5px; font-weight: 600; color: var(--xl-color-secondary-text); }
.dates__group:first-of-type { margin-top: 0; }
.dates__sh h2 { margin: 0; font-size: 13px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--xl-color-secondary-text); }
.dates__ln { flex: 1; height: 1px; background: var(--xl-color-border); }
.dates__card {
  list-style: none; margin: 0; padding: 8px 22px; background: #fff; border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg); box-shadow: var(--xl-shadow-1);
}
.dates__row { display: flex; align-items: center; justify-content: space-between; gap: 14px; padding: 15px 0; border-bottom: 1px solid var(--xl-color-border-subtle); }
.dates__row:last-child { border-bottom: 0; }
.dates__when { display: inline-flex; align-items: center; gap: 10px; font-size: 15px; font-weight: 600; }
/* A date that has been is part of what was scheduled, only quieter; a
   cancelled one is struck through so it cannot be read as on. */
.dates__row--done .dates__when { color: var(--xl-color-secondary-text); font-weight: 500; }
.dates__row--cancelled .dates__when { color: var(--xl-color-secondary-text); text-decoration: line-through; }
.dates__sr { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
@media (max-width: 600px) { .dates__row { flex-direction: column; align-items: flex-start; } }
</style>
